import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { COLORS } from '../../../utils/constants';
import './AuthForm.css';

const AuthForm = ({ mode, onSuccess, referralCode }) => {
  const { login, register } = useAuth();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    phone_number: '',
    referral_code: referralCode || '' // Initialize with prop
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [frozenInfo, setFrozenInfo] = useState(null);

  // Update referral code when prop changes
  useEffect(() => {
    if (referralCode && mode === 'register') {
      setFormData(prev => ({
        ...prev,
        referral_code: referralCode
      }));
      console.log('Referral code set in form:', referralCode);
    }
  }, [referralCode, mode]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setMessage('');
    setFrozenInfo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    setFrozenInfo(null);

    console.log('Submitting registration with data:', formData);

    try {
      if (mode === 'login') {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          onSuccess();
        } else {
          setError(result.error);
        }
      } else {
        const result = await register(formData);
        console.log('Registration result:', result);
        if (result.success) {
          setMessage('Registration successful! Please check your email for verification.');
          // Don't close modal immediately - show success message
          setTimeout(() => {
            onSuccess();
          }, 3000);
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
      if (err.isFrozen) {
        setFrozenInfo({ reason: err.reason, supportLink: err.supportLink });
      } else {
        setError(err.message || 'An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      {message && (
        <div className="auth-message" style={{ borderColor: COLORS.PRIMARY }}>
          {message}
        </div>
      )}
      
      {frozenInfo && (
        <div className="auth-error" style={{ borderLeft: '4px solid #dc3545', background: '#fff5f5', padding: '12px 14px' }}>
          <strong>🔒 Account Frozen</strong>
          {frozenInfo.reason && frozenInfo.reason !== 'No reason provided' && (
            <p style={{ margin: '6px 0 0' }}>
              <strong>Reason:</strong> {frozenInfo.reason}
            </p>
          )}
          <p style={{ margin: '8px 0 0' }}
            dangerouslySetInnerHTML={{ __html: frozenInfo.supportLink }}
          />
        </div>
      )}

      {error && (
        <div className="auth-error">
          {error}
        </div>
      )}

      {mode === 'register' && (
        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            type="text"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            style={{ borderColor: COLORS.BORDER }}
          />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          style={{ borderColor: COLORS.BORDER }}
        />
      </div>

      {mode === 'register' && (
        <div className="form-group">
          <label htmlFor="phone_number">Phone Number</label>
          <input
            type="tel"
            id="phone_number"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            required
            style={{ borderColor: COLORS.BORDER }}
          />
        </div>
      )}

      {/* ✅ PASSWORD FIELD — with Forgot Password link on login mode */}
      <div className="form-group">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '5px'
        }}>
          <label htmlFor="password" style={{ margin: 0 }}>Password</label>
          {mode === 'login' && (
            <a
              href="/forgot-password"
              style={{
                fontSize: '0.8rem',
                color: COLORS.PRIMARY,
                textDecoration: 'none',
                fontWeight: '500',
              }}
              onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
              onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
            >
              Forgot password?
            </a>
          )}
        </div>
        <div className="password-input-wrapper">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            minLength={6}
            style={{ borderColor: COLORS.BORDER }}
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword(!showPassword)}
            disabled={loading}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
      </div>

      {mode === 'register' && (
        <div className="form-group">
          <label htmlFor="referral_code">
            Referral Code (Optional)
            {formData.referral_code && <span style={{ color: COLORS.PRIMARY }}> ✓ Applied</span>}
          </label>
          <input
            type="text"
            id="referral_code"
            name="referral_code"
            value={formData.referral_code}
            onChange={handleChange}
            style={{ 
              borderColor: formData.referral_code ? COLORS.PRIMARY : COLORS.BORDER,
              backgroundColor: formData.referral_code ? 'rgba(21, 167, 89, 0.05)' : 'white'
            }}
            placeholder="Enter referral code"
          />
        </div>
      )}

      <button 
        type="submit" 
        className="auth-submit-button"
        disabled={loading}
        style={{ 
          backgroundColor: loading ? COLORS.BORDER : COLORS.PRIMARY,
          cursor: loading ? 'not-allowed' : 'pointer'
        }}
      >
        {loading ? 'Processing...' : (mode === 'login' ? 'Login' : 'Sign Up')}
      </button>
    </form>
  );
};

export default AuthForm;